/**
 * Payment Modal Component
 *
 * Handles payment processing with change calculation for Costa Rican Colones.
 * Supports cash, card, transfer, and credit payment methods.
 */

import * as React from 'react'
import { CreditCard, Banknote, Building2, Clock, Smartphone, FileCheck, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'

export enum PaymentMethod {
  CASH = 'cash',
  CARD = 'card',
  TRANSFER = 'transfer',
  SINPE = 'sinpe',
  CHECK = 'check',
  OTHER = 'other',
  CREDIT = 'credit',
}

const getPaymentMethodLabel = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.CASH: return t('payment.cash')
    case PaymentMethod.CARD: return t('payment.card')
    case PaymentMethod.TRANSFER: return t('payment.transfer')
    case PaymentMethod.SINPE: return t('payment.sinpe')
    case PaymentMethod.CHECK: return t('payment.check')
    case PaymentMethod.OTHER: return t('payment.other')
    case PaymentMethod.CREDIT: return t('payment.credit')
  }
}

const PAYMENT_METHOD_ICONS: Record<PaymentMethod, React.ReactNode> = {
  [PaymentMethod.CASH]: <Banknote className="h-5 w-5" />,
  [PaymentMethod.CARD]: <CreditCard className="h-5 w-5" />,
  [PaymentMethod.TRANSFER]: <Building2 className="h-5 w-5" />,
  [PaymentMethod.SINPE]: <Smartphone className="h-5 w-5" />,
  [PaymentMethod.CHECK]: <FileCheck className="h-5 w-5" />,
  [PaymentMethod.OTHER]: <MoreHorizontal className="h-5 w-5" />,
  [PaymentMethod.CREDIT]: <Clock className="h-5 w-5" />,
}

interface PaymentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  total: number
  clientName?: string
  clientHasCredit?: boolean
  availableCredit?: number
  creditDays?: number
  onConfirmPayment: (data: PaymentData) => void | Promise<void>
  isProcessing?: boolean
  initialPaymentMethod?: PaymentMethod
}

export interface PaymentData {
  paymentMethod: PaymentMethod
  amountReceived: number
  change: number
  termDays?: number
}

export function PaymentModal({
  open,
  onOpenChange,
  total,
  clientName,
  clientHasCredit = false,
  availableCredit = 0,
  creditDays = 30,
  onConfirmPayment,
  isProcessing = false,
  initialPaymentMethod = PaymentMethod.CASH,
}: PaymentModalProps) {
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>(initialPaymentMethod)
  const [amountReceived, setAmountReceived] = React.useState<string>('')
  const [termDays, setTermDays] = React.useState<number>(creditDays)

  // Calculate change
  const receivedNumber = parseFloat(amountReceived) || 0
  const change = Math.max(0, receivedNumber - total)
  const isValidPayment = paymentMethod === PaymentMethod.CREDIT
    ? availableCredit >= total
    : receivedNumber >= total

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setPaymentMethod(initialPaymentMethod)
      setAmountReceived('')
      setTermDays(creditDays)
    }
  }, [open, creditDays, initialPaymentMethod])

  // Auto-fill exact amount for non-cash payments
  React.useEffect(() => {
    if (paymentMethod !== PaymentMethod.CASH) {
      setAmountReceived(total.toString())
    }
  }, [paymentMethod, total])

  const handleConfirm = async () => {
    const paymentData: PaymentData = {
      paymentMethod,
      amountReceived: receivedNumber,
      change,
      termDays: paymentMethod === PaymentMethod.CREDIT ? termDays : undefined,
    }
    await onConfirmPayment(paymentData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md flex flex-col max-h-[85dvh]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('payment.processPayment')}</DialogTitle>
          <DialogDescription>
            {clientName ? `${t('payment.client')}: ${clientName}` : t('payment.completeSale')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 -mx-6 px-6">
          {/* Total and Payment Method Summary */}
          <div className="text-center bg-muted/50 p-4 rounded-lg space-y-3">
            <p className="text-sm text-muted-foreground">{t('payment.totalToPay')}</p>
            <p className="text-4xl font-bold text-primary">
              {formatCurrency(total)}
            </p>
            <div className="flex items-center justify-center gap-2 pt-2 border-t border-muted">
              {PAYMENT_METHOD_ICONS[paymentMethod]}
              <p className="text-sm font-medium">{getPaymentMethodLabel(paymentMethod)}</p>
            </div>
          </div>

          {/* Processing State */}
          {isProcessing && (
            <div className="flex items-center justify-center gap-3 py-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <Spinner size="sm" className="text-blue-600 dark:text-blue-400" />
              <div className="text-center">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {t('payment.processing')}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  {t('payment.processingDescription')}
                </p>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="space-y-2">
            <Label>{t('payment.paymentMethod')}</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.values(PaymentMethod)
                .filter((method) => method !== PaymentMethod.OTHER)
                .filter((method) => method !== PaymentMethod.CREDIT || clientHasCredit)
                .map((method) => (
                  <Button
                    key={method}
                    type="button"
                    variant={paymentMethod === method ? 'default' : 'outline'}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                    onClick={() => setPaymentMethod(method)}
                  >
                    {PAYMENT_METHOD_ICONS[method]}
                    <span className="text-xs">{getPaymentMethodLabel(method)}</span>
                  </Button>
                ))}
            </div>
          </div>

          {/* Credit info */}
          {paymentMethod === PaymentMethod.CREDIT && (
            <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('payment.availableCredit')}:</span>
                <span className="font-medium">{formatCurrency(availableCredit)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="termDays" className="text-sm whitespace-nowrap">
                  {t('payment.termDays')}:
                </Label>
                <Input
                  id="termDays"
                  type="number"
                  value={termDays || ''}
                  onChange={(e) => setTermDays(parseInt(e.target.value) || 0)}
                  className="w-20"
                  min={1}
                  max={365}
                  placeholder="30"
                />
              </div>
              {availableCredit < total && (
                <p className="text-xs text-orange-700">
                  {t('payment.insufficientCredit')} {formatCurrency(total - availableCredit)} {t('payment.more')}.
                </p>
              )}
            </div>
          )}

          {/* Cash Payment */}
          {paymentMethod === PaymentMethod.CASH && (
            <>
              <div className="space-y-2">
                <Label htmlFor="amountReceived">{t('payment.amountReceived')}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    ₡
                  </span>
                  <Input
                    id="amountReceived"
                    type="number"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    className="pl-8 text-lg font-mono"
                    placeholder="0"
                    autoFocus
                  />
                </div>
              </div>

              <Separator />

              {/* Change calculation */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('payment.amountReceived')}:</span>
                  <span className="">{formatCurrency(receivedNumber)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{t('payment.total')}:</span>
                  <span className="">-{formatCurrency(total)}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="font-medium">{t('payment.change')}:</span>
                  <Badge
                    variant={change > 0 ? 'success' : receivedNumber >= total ? 'default' : 'destructive'}
                    className="text-2xl px-4 py-2"
                  >
                    {formatCurrency(change)}
                  </Badge>
                </div>
              </div>
            </>
          )}

          {/* Card/Transfer/SINPE/Check/Other Payment - Simple confirmation */}
          {(paymentMethod === PaymentMethod.CARD || paymentMethod === PaymentMethod.TRANSFER || paymentMethod === PaymentMethod.SINPE || paymentMethod === PaymentMethod.CHECK || paymentMethod === PaymentMethod.OTHER) && (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                {paymentMethod === PaymentMethod.CARD
                  ? t('payment.processCard')
                  : t('payment.confirmTransfer')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 shrink-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            {t('payment.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!isValidPayment || isProcessing}
            className="w-full sm:w-auto"
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t('payment.processing')}
              </>
            ) : (
              paymentMethod === PaymentMethod.CREDIT
                ? t('payment.confirmCreditSale')
                : `${t('payment.confirm')} ${formatCurrency(total)}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
